import React, { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import PropTypes from "prop-types"
import * as Yup from "yup"
import { useFormik } from "formik"
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Input,
  FormFeedback,
  Label,
} from "reactstrap"

//Import Breadcrumb
import Breadcrumbs from "components/Common/Breadcrumb"

//redux
import {
  getApiConfigurationById,
  editApiConfiguration,
  addApiConfiguration,
} from "helpers/UAM/apiConfig.helper"
import ToastDialog from "common/ToastDialog"

const AddApiConfiguration = (props) => {
  const navigate = useNavigate()

  const location = useLocation()
  const pathnameSegments = location.pathname.split("/")
  const lastSegment = pathnameSegments[pathnameSegments.length - 1]
  const { roleName } = JSON.parse(localStorage.getItem("user"))

  //meta title
  let formattedLastSegment = lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1).toLowerCase();
  document.title = `${formattedLastSegment} API Configuration | DMS`

  const urlParams = new URLSearchParams(window.location.search)
  const encodedId = urlParams.get("id")
  const decodedId = atob(encodedId)
  const [showViewTemplate, setShowViewTemplate] = useState(false)
  const [showEditTemplate, setShowEditTemplate] = useState(true)
  const [toastInfo, setToastInfo] = useState({
    show: false,
  })

  // validation
  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      url: "",
      purpose:"",
      apiUsername: "",
      apiPassword: "",
      apiPort: "",
      apiShortCode:"",
      keyValues: "",
      source: "",
    },
    validationSchema: Yup.object({
      url: Yup.string().required("URL is Required"),
      purpose: Yup.string().required("Please Enter Purpose"),
      apiUsername: Yup.string(),
      apiPassword: Yup.string()
    }),
    onSubmit: async (values) => {
      if (lastSegment == "edit") {
        const updateTemplate = {
          id: decodedId,
          url: values.url,
          purpose: values.purpose,
          apiShortCode: values.apiShortCode || null,
          apiUsername: values.apiUsername,
          apiPassword: values.apiPassword,
          apiPort: values.apiPort || null,
          keyValues: values.keyValues || null,
          source: values.source || null,
        }
        const apiRes = await editApiConfiguration(updateTemplate, navigate)

        if (apiRes.status) {
          navigate(`/${roleName}/api-config`)
          validation.resetForm()
        } else {
          setToastInfo({
            show: true,
            type: "error",
            message: apiRes.message,
          }) // reset toast
        }
      } else {
        const newTemplate = {
          url: values.url,
          purpose: values.purpose,
          apiShortCode: values.apiShortCode || null,
          apiUsername: values.apiUsername,
          apiPassword: values.apiPassword,
          apiPort: values.apiPort || null,
          keyValues: values.keyValues || null,
          source: values.source || null,
        }

        const apiRes = await addApiConfiguration(newTemplate, navigate)
        if (apiRes.status) {
          navigate(`/${roleName}/api-config`)
          validation.resetForm()
        } else {
          setToastInfo({
            show: true,
            type: "error",
            message: apiRes.message,
          }) // reset toast
        }
      }
    },
  })

  const setDataInFormik = data => {
    validation.setFieldValue("url", data.url)
    validation.setFieldValue("purpose", data.purpose)
    validation.setFieldValue("apiUsername", data.apiUsername)
    validation.setFieldValue("apiPassword", data.apiPassword)
    validation.setFieldValue("apiPort", data.apiPort)
    validation.setFieldValue("apiShortCode", data.apiShortCode)
    validation.setFieldValue("keyValues", data.keyValues)
    validation.setFieldValue("source", data.source)

    if (lastSegment == "view") {
      setShowViewTemplate(true)
      setShowEditTemplate(false)
    } else {
      setShowViewTemplate(false)
      setShowEditTemplate(true)
    }
  }
  const getApiconfigurationInfo = async id => {
    const apiRes = await getApiConfigurationById({ id })
    if (apiRes.status) {
      setDataInFormik(apiRes.data)
    }
  }

  useEffect(() => {
    if (decodedId) {
      getApiconfigurationInfo(decodedId)
    }
  }, [encodedId])

  const navigateBack = () => {
    navigate(`/${roleName}/api-config`)
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title="Configuration"
            breadcrumbItem={`${lastSegment.toUpperCase()} Api Configuration`}
          />
          <Row className="vertical-scroll">
            <Col xs="12">
              <Card>
                <CardBody>
                  {showEditTemplate && (
                    <form
                      onSubmit={e => {
                        e.preventDefault()
                        validation.handleSubmit()
                        return false
                      }}
                    >
                      <Row className="mb-4">
                        <Col sm={5}>
                          <Label
                            htmlFor="horizontal-email-Input"
                            className="col-sm-3 col-form-label required"
                          >
                            URL
                          </Label>
                          <Input
                            type="text"
                            className="form-control"
                            name="url"
                            id="horizontal-url-Input"
                            placeholder="Enter URL"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.url || ""}
                            invalid={
                              validation.touched.url && validation.errors.url
                                ? true
                                : false
                            }
                          />
                          {validation.touched.url && validation.errors.url ? (
                            <FormFeedback type="invalid">
                              {validation.errors.url}
                            </FormFeedback>
                          ) : null}
                        </Col>
                        <Col sm={5}>
                          <Label
                            htmlFor="horizontal-notification-Input"
                            className="col-sm-3 col-form-label required"
                          >
                            Purpose
                          </Label>
                          <Input
                            type="text"
                            className="form-control"
                            name="purpose"
                            id="horizontal-email-Input"
                            placeholder="Enter Purpose "
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.purpose || ""}
                            invalid={
                              validation.touched.purpose &&
                                validation.errors.purpose
                                ? true
                                : false
                            }
                          />
                          {validation.touched.purpose &&
                            validation.errors.purpose ? (
                            <FormFeedback type="invalid">
                              {validation.errors.purpose}
                            </FormFeedback>
                          ) : null}
                        </Col>
                      </Row>
                      <Row className="mb-4">
                        <Col sm={5}>
                          <Label
                            htmlFor="horizontal-email-Input"
                            className="col-sm-3 col-form-label required"
                          >
                            API UserName
                          </Label>
                          <Input
                            type="text"
                            className="form-control"
                            name="apiUsername"
                            id="horizontal-url-Input"
                            placeholder="Enter Api Username"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.apiUsername || ""}
                            invalid={
                              validation.touched.apiUsername &&
                                validation.errors.apiUsername
                                ? true
                                : false
                            }
                          />
                          {validation.touched.apiUsername &&
                            validation.errors.apiUsername ? (
                            <FormFeedback type="invalid">
                              {validation.errors.apiUsername}
                            </FormFeedback>
                          ) : null}
                        </Col>
                        <Col sm={5}>
                          <Label
                            htmlFor="horizontal-notification-Input"
                            className="col-sm-3 col-form-label required"
                          >
                            API Password
                          </Label>
                          <Input
                            type="text"
                            className="form-control"
                            name="apiPassword"
                            id="horizontal-email-Input"
                            placeholder="Enter Api Password"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.apiPassword || ""}
                            invalid={
                              validation.touched.apiPassword &&
                                validation.errors.apiPassword
                                ? true
                                : false
                            }
                          />
                          {validation.touched.apiPassword &&
                            validation.errors.apiPassword ? (
                            <FormFeedback type="invalid">
                              {validation.errors.apiPassword}
                            </FormFeedback>
                          ) : null}
                        </Col>
                      </Row>
                      <Row className="mb-4">
                        <Col sm={5}>
                          <Label
                            htmlFor="horizontal-email-Input"
                            className="col-sm-3 col-form-label"
                          >
                            API Port
                          </Label>
                          <Input
                            type="text"
                            className="form-control"
                            name="apiPort"
                            id="horizontal-url-Input"
                            placeholder="Enter Api Port"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.apiPort || ""}
                          />
                        </Col>
                        <Col sm={5}>
                          <Label
                            htmlFor="horizontal-notification-Input"
                            className="col-sm-3 col-form-label"
                          >
                            API ShortCode
                          </Label>
                          <Input
                            type="text"
                            className="form-control"
                            name="apiShortCode"
                            id="horizontal-email-Input"
                            placeholder="Enter Api ShortCode "
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.apiShortCode || ""}
                          />
                        </Col>
                      </Row>
                      <Row className="mb-4">
                        <Col sm={5}>
                          <Label
                            htmlFor="horizontal-email-Input"
                            className="col-sm-3 col-form-label"
                          >
                            Key Values
                          </Label>
                          <Input
                            type="text"
                            className="form-control"
                            name="keyValues"
                            id="horizontal-url-Input"
                            placeholder="Enter Key Values"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.keyValues || ""}
                          />
                        </Col>
                        <Col sm={5}>
                          <Label
                            htmlFor="horizontal-notification-Input"
                            className="col-sm-3 col-form-label"
                          >
                            Source
                          </Label>
                          <Input
                            type="text"
                            className="form-control"
                            name="source"
                            id="horizontal-email-Input"
                            placeholder="Enter Source "
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.source || ""}
                          />
                        </Col>
                      </Row>

                      <Row>
                        <Col>
                          <div
                            className="text-end"
                            style={{ margin: "20px 17% 0px 0px" }}
                          >
                            <button
                              type="submit"
                              className="btn btn-outline-secondary"

                              onClick={() => {
                                navigateBack()
                              }}
                            >
                              Back
                            </button>
                            <button
                              type="submit"
                              className="btn btn-success save-customer"
                              style={{ marginLeft: "7px" }}
                            >
                              Save
                            </button>

                          </div>
                        </Col>
                      </Row>
                    </form>
                  )}

                  {showViewTemplate && (
                    <div>
                      <Row className="mb-4">
                        <Col sm={6} md="4">
                          <Label className="col-sm-3 col-form-label label label-styling">
                            URL
                          </Label>

                          {validation.values.url ? (
                            <p>{validation.values.url}</p>
                          ) : (
                            <p>-</p>
                          )}
                        </Col>
                        <Col sm={6} md="4">
                          <Label
                            htmlFor="horizontal-notification-Input"
                            className="col-sm-3 col-form-label label label-styling"
                          >
                            Purpose
                          </Label>
                          {validation.values.purpose ? (
                            <p>{validation.values.purpose}</p>
                          ) : (
                            <p>-</p>
                          )}
                        </Col>
                        <Col sm={6} md="4">
                          <Label className="col-sm-3 col-form-label label label-styling">
                            API UserName
                          </Label>

                          {validation.values.apiUsername ? (
                            <p>{validation.values.apiUsername}</p>
                          ) : (
                            <p>-</p>
                          )}
                        </Col>

                        <Col sm={6} md='4'>
                          <Label
                            htmlFor="horizontal-notification-Input"
                            className="col-sm-3 col-form-label label label-styling"
                          >
                            API Password
                          </Label>
                          {validation.values.apiPassword ? (
                            <p>{validation.values.apiPassword}</p>
                          ) : (
                            <p>-</p>
                          )}
                        </Col>

                        <Col sm={6} md='4'>
                          <Label className="col-sm-3 col-form-label label label-styling">
                            API Port
                          </Label>

                          {validation.values.apiPort ? (
                            <p>{validation.values.apiPort || "-"}</p>
                          ) : (
                            <p>-</p>
                          )}
                        </Col>
                        <Col sm={6} md='4'>
                          <Label className="col-sm-3 col-form-label label label-styling">
                            API ShortCode
                          </Label>

                          {validation.values.apiShortCode ? (
                            <p>{validation.values.apiShortCode || "-"}</p>
                          ) : (
                            <p>-</p>
                          )}
                        </Col>

                        <Col sm={6} md='4'>
                          <Label className="col-sm-3 col-form-label label label-styling">
                            Key Values
                          </Label>

                          {validation.values.keyValues ? (
                            <p>{validation.values.keyValues || "-"}</p>
                          ) : (
                            <p>-</p>
                          )}
                        </Col>
                        <Col sm={6} md='4'>
                          <Label className="col-sm-3 col-form-label label label-styling">
                            Source
                          </Label>

                          {validation.values.source ? (
                            <p>{validation.values.source || "-"}</p>
                          ) : (
                            <p>-</p>
                          )}
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <div>
                            <button
                              className="btn btn-outline-secondary"
                              onClick={() => {
                                navigateBack()
                              }}
                            >
                              Back
                            </button>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      {toastInfo.show && (
        <ToastDialog
          message={toastInfo.message || ""}
          showToast={toastInfo.show}
          role={toastInfo.role}
          onCloseFn={() => setToastInfo({ show: false })}
        ></ToastDialog>
      )}
    </React.Fragment>
  )
}

AddApiConfiguration.propTypes = {
  history: PropTypes.object,
}

export default AddApiConfiguration
